import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Button,
    IconButton,
    Alert,
    TextField,
    Autocomplete,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    LocationCity as CityIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ArrowUpward as MoveUpIcon,
    ArrowDownward as MoveDownIcon,
} from '@mui/icons-material';
import axios from 'axios';
import cityService from '../../services/cityService';

const CityClientsSection = () => {
    const [expanded, setExpanded] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [associatedClients, setAssociatedClients] = useState([]);
    
    // Autocomplete for searching and adding new wholesale clients
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOptions, setSearchOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedClientOption, setSelectedClientOption] = useState(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Fetch active cities available for trips
    const fetchCities = async () => {
        try {
            setLoadingCities(true);
            setError('');
            const data = await cityService.getCities({
                paginate: 'false',
                is_available_for_trips: 'true',
                active_only: 'true'
            });
            setCities(data);
        } catch (err) {
            setError('Error al obtener la lista de ciudades.');
        } finally {
            setLoadingCities(false);
        }
    };

    // Fetch clients associated with the selected city
    const fetchAssociatedClients = async (cityId) => {
        if (!cityId) {
            setAssociatedClients([]);
            return;
        }
        try {
            setLoadingClients(true);
            setError('');
            // Requesting large page size to fetch all associated clients
            const res = await axios.get(`/api/clients/?delivery_city=${cityId}&client_type=wholesale&page_size=1000`);
            const data = res.data?.results || res.data || [];
            setAssociatedClients(data);
        } catch (err) {
            setError('Error al obtener los clientes asociados a la ciudad.');
        } finally {
            setLoadingClients(false);
        }
    };

    // Search wholesale clients that are not already associated with the selected city
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchOptions([]);
            return;
        }
        const delayDebounce = setTimeout(async () => {
            try {
                setSearching(true);
                const res = await axios.get(`/api/clients/?client_type=wholesale&search=${encodeURIComponent(searchQuery)}&page_size=20`);
                const results = res.data?.results || res.data || [];
                
                // Exclude clients that are already in the list
                const associatedIds = new Set(associatedClients.map(c => c.id));
                const filteredOptions = results.filter(c => !associatedIds.has(c.id));
                setSearchOptions(filteredOptions);
            } catch (err) {
                console.error('Error searching clients:', err);
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery, associatedClients]);

    useEffect(() => {
        if (expanded) {
            fetchCities();
        }
    }, [expanded]);

    useEffect(() => {
        if (selectedCity) {
            fetchAssociatedClients(selectedCity.id);
        } else {
            setAssociatedClients([]);
        }
    }, [selectedCity]);

    const handleAddClient = () => {
        if (!selectedClientOption) return;
        
        // Append client to local associated list
        setAssociatedClients((prev) => [
            ...prev,
            selectedClientOption
        ]);
        setSelectedClientOption(null);
        setSearchQuery('');
        setError('');
    };

    const handleRemoveClient = (index) => {
        setAssociatedClients((prev) => prev.filter((_, i) => i !== index));
    };

    const handleMoveClient = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === associatedClients.length - 1) return;

        const newClients = [...associatedClients];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Swap
        const temp = newClients[index];
        newClients[index] = newClients[targetIndex];
        newClients[targetIndex] = temp;

        setAssociatedClients(newClients);
    };

    const handleSaveOrder = async () => {
        if (!selectedCity) return;
        try {
            setSaving(true);
            setError('');
            setSuccess('');
            
            const clientIds = associatedClients.map(c => c.id);
            await axios.post('/api/clients/reorder-by-city/', {
                city_id: selectedCity.id,
                client_ids: clientIds
            });
            
            setSuccess('Asociación y orden guardados correctamente.');
            // Refresh
            fetchAssociatedClients(selectedCity.id);
        } catch (err) {
            setError(err.response?.data?.error || 'Error al guardar los cambios.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CityIcon color="primary" />
                    <Typography sx={{ fontWeight: 600 }}>Clientes por Ciudad</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* City Selector */}
                    <Box sx={{ maxWidth: 400 }}>
                        <Autocomplete
                            options={cities}
                            getOptionLabel={(option) => option.province ? `${option.name}, ${option.province.name}` : option.name}
                            value={selectedCity}
                            onChange={(_, newValue) => setSelectedCity(newValue)}
                            loading={loadingCities}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Seleccionar Ciudad"
                                    variant="outlined"
                                    placeholder="Buscar ciudad habilitada..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {selectedCity && (
                        <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                Reparto en: {selectedCity.name} ({associatedClients.length} clientes)
                            </Typography>

                            {/* Client Search and Add */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, maxWidth: 600 }}>
                                <Autocomplete
                                    sx={{ flexGrow: 1 }}
                                    options={searchOptions}
                                    getOptionLabel={(option) => `${option.name} (${option.cuit})`}
                                    value={selectedClientOption}
                                    onChange={(_, newValue) => setSelectedClientOption(newValue)}
                                    inputValue={searchQuery}
                                    onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
                                    loading={searching}
                                    placeholder="Buscar cliente por nombre o CUIT..."
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar Cliente Mayorista"
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {searching ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddClient}
                                    disabled={!selectedClientOption}
                                    startIcon={<AddIcon />}
                                    sx={{ px: 3 }}
                                >
                                    Agregar
                                </Button>
                            </Box>

                            {loadingClients ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {associatedClients.length === 0 ? (
                                        <Typography sx={{ color: '#666', fontStyle: 'italic', my: 2 }}>
                                            No hay clientes asociados a esta ciudad. Use el buscador de arriba para agregar.
                                        </Typography>
                                    ) : (
                                        <List sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0, mb: 3 }}>
                                            {associatedClients.map((client, index) => (
                                                <ListItem
                                                    key={client.id}
                                                    divider={index < associatedClients.length - 1}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? '#fdfdfd' : '#f7f9fa',
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={`${index + 1}. ${client.name}`}
                                                        secondary={`${client.delivery_address} • CUIT: ${client.cuit}`}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="move-up"
                                                            onClick={() => handleMoveClient(index, 'up')}
                                                            disabled={index === 0}
                                                        >
                                                            <MoveUpIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="move-down"
                                                            onClick={() => handleMoveClient(index, 'down')}
                                                            disabled={index === associatedClients.length - 1}
                                                            sx={{ mr: 1 }}
                                                        >
                                                            <MoveDownIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="delete"
                                                            color="error"
                                                            onClick={() => handleRemoveClient(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveOrder}
                                        disabled={saving}
                                        sx={{ minWidth: 150 }}
                                    >
                                        {saving ? 'Guardando...' : 'Guardar Orden'}
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default CityClientsSection;
